<template>
    <div class="sleep-chart-details">
        <div class="chart-container">
            <canvas id="sleepCircleChart"></canvas>
            <div class="chart-label">
                <div style="margin-top: -8.7rem; position: relative;text-align: center;">
                    <span>Hours of Sleep</span>
                    <div>
                        <span style="font-size: 1.75rem;font-weight: bold;">
                          <IconBedTimeDown :size="'1.3rem'" :color="'#e10e0e91'" v-if="this.sleepMinutes < this.sleepStats.prevSleepMinutes"/>
                          <IconBedTimeUp :size="'1.3rem'" :color="'#6cc7c1'" v-else />{{formatMinutesToDuration(this.sleepMinutes)}}</span>
                    </div>
                    <small style="color: #aaa;">{{formatMinutesToDuration(this.sleepStats.prevSleepMinutes)}}</small>
                </div>
            </div>
        </div>

        <div class="info" v-if="sleepPerformance">
            <div class="w-30">
                <div class="title">Wake Events
                    <IconWakeEvents :size="'1.4rem'" :color="'#aabdcf'" /> 
                </div>
                <div class="stats-container">
                    <span class="points">{{ sleepData.wake_events }}</span>
                    <span class="text-uppercase" :class="sleepPerformance.wakeEvent">{{ sleepPerformance.wakeEvent
                        }}</span>
                </div>
            </div>

            <div class="w-35">
                <div class="title">
                    <span>Deep Sleep Continuity 
                        <IconDeepSleepCont :size="'1.3rem'" :color="'#aabdcf'" /> 
                    </span> 
                </div>
                <div class="stats-container">
                    <span class="points">{{ sleepData.deep_sleep_part_count }}</span>
                    <span class="text-uppercase" :class="sleepPerformance.deepSleep">{{ sleepPerformance.deepSleep
                        }}</span>
                </div>
            </div>

            <div class="w-30">
                <div class="title">Breathing Quality
                    <IconBreathQuality :size="'1.3rem'" :color="'#aabdcf'"/> 
                </div>
                <div class="stats-container">
                    <span class="points">{{ sleepData.breathing_quality }}</span>
                    <span class="text-uppercase" :class="sleepPerformance.breathQuality">{{
                        sleepPerformance.breathQuality }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Chart } from 'chart.js';
import Utils from "@/helpers/utils";
import IconBedTimeDown from "@/shared/Controls/Icons/IconBedTimeDown.vue";
import IconBedTimeUp from "@/shared/Controls/Icons/IconBedTimeUp.vue";
import IconWakeEvents from "@/shared/Controls/Icons/IconWakeEvents.vue";
import IconDeepSleepCont from "@/shared/Controls/Icons/IconDeepSleepCont.vue";
import IconBreathQuality from "@/shared/Controls/Icons/IconBreathQuality.vue";


export default {
    name: "SleepCircleChart",
    components: {
        IconBedTimeDown,
        IconBedTimeUp,
        IconWakeEvents,
        IconDeepSleepCont,
        IconBreathQuality,
    },
    data() {
        return {
            sleepPerformance: null,
            sleepMinutes: 0,
        }
    },
    props: {
        sleepData: {
            type: Object,
            required: false,
        },
        sleepStats: {
            type: Object,
            required: false,
        }
    },
    mounted() {
        this.prepareStats();
        this.renderChart();
    },
    methods: {
        prepareStats() {
            //console.log('Preparing sleep stats with data:', this.sleepData, 'and stats:', this.sleepStats);
            this.sleepMinutes = this.sleepData.sleep_minutes-this.sleepData.awake_minutes;
            let breathStatus = '';
            if(this.sleepStats.maxBreathingQuality == 0 && this.sleepStats.minBreathingQuality == 0) {
                breathStatus = 'typical';    
            } else if(this.sleepData.breathing_quality >= this.sleepStats.maxBreathingQuality) {
                breathStatus = 'good';
            } else if(this.sleepData.breathing_quality >= this.sleepStats.minBreathingQuality){
                breathStatus = 'typical';
            } else {
                breathStatus = 'poor';
            }

            this.sleepPerformance = {
                wakeEvent: this.getStatusBasedOnValue(this.sleepStats.maxWakeEvents, this.sleepStats.minWakeEvents, this.sleepData.wake_events),
                deepSleep: this.getStatusBasedOnValue(this.sleepStats.maxDeepSleepContinuty, this.sleepStats.minDeepSleepContinuty, this.sleepData.deep_sleep_part_count),
                breathQuality: breathStatus,
            };
        },
        getStatusBasedOnValue(maxVal, minVal, currentVal) {
            if (currentVal <= minVal) {
                return 'good';
            }

            if (currentVal > minVal && currentVal <= maxVal) {
                return 'typical';
            }

            if (currentVal > maxVal) {
                return 'poor';
            }
        },
        formatMinutesToDuration(sleepMins) {
            let seconds = sleepMins * 60;
            return Utils.formatDurationInHrsAndMins(seconds);
        },
        renderChart() {
            const ctx = document.getElementById('sleepCircleChart').getContext('2d');
            new Chart(ctx, {
                type: 'doughnut',
                data: {
                    labels: ['Deep Sleep', 'Light Sleep', 'REM Sleep','Awake'],
                    datasets: [
                        {
                            data: [this.sleepData.deep_sleep_minutes, this.sleepData.light_sleep_minutes, this.sleepData.rem_sleep_minutes,this.sleepData.awake_minutes],
                            backgroundColor: ['#073e47', '#c2dbdd', '#6bb4b2','#ffd700'],
                            borderWidth: 0
                        }
                    ]
                },
                options: {
                    cutout: '85%',
                    plugins: {
                        legend: {
                            display: false
                        }
                    }
                }
            });
        }
    },
    watch: {
        sleepData() {
            this.prepareStats();
            this.renderChart();
        },
        sleepStats() {
            this.prepareStats();
            this.renderChart();
        }
    }
};
</script>

<style lang="scss" scoped>
.sleep-chart-details {
    padding: 1rem;
    background-color: #121212;
    height: 20rem;
    border-radius: .5rem;
}

.chart-container {
    height: 12rem;
    width: 12rem;
    margin: 0 auto;
}

.info {
    margin-top: 1rem;
    display: flex;
    justify-content: space-around;
    font-size: 0.85rem;
    padding-left: 5rem;
}

.info-item {
    text-align: center;
}

.info-item span {
    display: block;
}

.good {
    color: #3EDA28;
    background-color: #445649;
    padding: 2px 6px;
    border-radius: 0.3rem;
    font-size: 0.7rem;
    font-weight: bold;
}

.poor {
    color: #AC0000;
    background-color: #341414;
    padding: 2px 6px;
    border-radius: 0.3rem;
    font-size: 0.7rem;
    font-weight: bold;
}

.typical {
    color: #E2E2E2;
    background-color: #393939;
    padding: 2px 6px;
    border-radius: 0.3rem;
    font-size: 0.7rem;
    font-weight: bold;
}

.stats-container {
    display: flex;
    align-items: center;
    gap: 0.65rem;
}

.points {
    font-size: 1.2rem;
    font-weight: bold;
    color: #ffffff;
}
.title {
    color: #aabdcf;
    font-size: 0.8rem;
}
</style>